import {
  Alert,
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import type { SxProps, Theme } from '@mui/material/styles'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import dayjs from 'dayjs'
import { useEffect, useMemo, useRef, useState } from 'react'

import { useSnackbar } from '@/hooks/useSnackbar'
import {
  useCreateCvbbMutation,
  useCvbbTypesLookupQuery,
  useLazyCvbbInvoicesLookupQuery,
  useLazyCvbbPContractsLookupQuery,
  useLazyCvbbSContractsLookupQuery,
  useLazyGetCvbbFormQuery,
  useUpdateCvbbMutation,
} from '@/store/api/procurementCvbbApi'
import type {
  CvbbFormSnapshot,
  CvbbLookupItem,
  CvbbWrite,
} from '@/store/api/procurementCvbbApi'
import { getErrorMessage } from '@/utils/error'

import {
  compactDialogContentSx,
  dialogHeaderBtnSx,
  dialogTitleActionsSx,
  dialogTitleSx,
  dialogTitleTextSx,
} from '../purchase/dialogStyle'

// ---------------------------------------------------------------------------
// Props — exact signature for orchestrator wiring (ListView imports this).
// ---------------------------------------------------------------------------
interface Props {
  open: boolean
  mode: 'new' | 'edit' | 'copy'
  cvbbId: number | null
  canEdit: boolean
  onClose: () => void
  onSaved: () => void
}

// ---------------------------------------------------------------------------
// Draft state.
// ---------------------------------------------------------------------------
interface DraftState {
  cvbb_id: string
  cvbb_no: string
  cvbb_date: string
  cvbb_type: string
  // contract routing (mutual exclusion) — one CVBB is tied to a single contract.
  p_contract: string
  s_contract: string
  invoice_no: string
  info_txt: string
  note: string
}

const EMPTY_DRAFT: DraftState = {
  cvbb_id: '',
  cvbb_no: '',
  cvbb_date: '',
  cvbb_type: '',
  p_contract: '',
  s_contract: '',
  invoice_no: '',
  info_txt: '',
  note: '',
}

type Opt = CvbbLookupItem
const SPAN2: SxProps<Theme> = { gridColumn: { xs: '1', sm: '1 / span 2' } }
const COL1: SxProps<Theme> = { gridColumn: { xs: '1', sm: '1' } }
const COL2: SxProps<Theme> = { gridColumn: { xs: '1', sm: '2' } }

/**
 * Procurement CVBB (Công văn & Biên bản) New / Edit / Copy dialog — converts
 * Access form `frmProcurementQryCVBBAdd` (Caption "Procurement - CVBB",
 * btnNew/btnEdit/btnCopy of frmProcurementMainSubFormCVBB).
 *
 * Layout follows the Access 2-column grid (mobile stacks to 1 column, no
 * fullScreen). Date DatePicker DD-MMM-YYYY. Combos are flat MUI Autocomplete
 * "CODE — NAME".
 *
 * Contract P / Contract S are mutually exclusive (Access VBA
 * p_contract_AfterUpdate / s_contract_AfterUpdate): choosing one clears the
 * other. On change the invoice_no RowSource is requeried for the resolved
 * contract. On save contract_no = p_contract || s_contract.
 */
export default function ProcurementCvbbFormDialog({
  open,
  mode,
  cvbbId,
  canEdit,
  onClose,
  onSaved,
}: Props) {
  const snack = useSnackbar()
  const isEdit = mode === 'edit'

  const [draft, setDraft] = useState<DraftState>(EMPTY_DRAFT)
  const [hydrating, setHydrating] = useState(false)

  // Mutations
  const [createCvbb, { isLoading: creating }] = useCreateCvbbMutation()
  const [updateCvbb, { isLoading: updating }] = useUpdateCvbbMutation()
  const saving = creating || updating

  // Lazy detail + lazy lookups
  const [fetchDetail] = useLazyGetCvbbFormQuery()
  const [fetchPContracts] = useLazyCvbbPContractsLookupQuery()
  const [fetchSContracts] = useLazyCvbbSContractsLookupQuery()
  const [fetchInvoices] = useLazyCvbbInvoicesLookupQuery()

  // Eager lookups
  const { data: typesData } = useCvbbTypesLookupQuery()

  const [pContractOptions, setPContractOptions] = useState<Opt[]>([])
  const [sContractOptions, setSContractOptions] = useState<Opt[]>([])
  const [invoiceOptions, setInvoiceOptions] = useState<Opt[]>([])

  const typeOptions = useMemo<Opt[]>(() => typesData?.items ?? [], [typesData])

  // Access ControlSource: cvbb_full_no = [cvbb_type] & "-" & [cvbb_no]
  const cvbbFullNo = useMemo(
    () => (draft.cvbb_type && draft.cvbb_no ? `${draft.cvbb_type}-${draft.cvbb_no}` : ''),
    [draft.cvbb_type, draft.cvbb_no],
  )

  // Access ControlSource: p_vender / s_client = DLookUp(supplier_name/client_name, ...)
  const pVendorLabel = useMemo(
    () => pContractOptions.find((o) => o.value === draft.p_contract)?.label ?? '',
    [pContractOptions, draft.p_contract],
  )
  const sClientLabel = useMemo(
    () => sContractOptions.find((o) => o.value === draft.s_contract)?.label ?? '',
    [sContractOptions, draft.s_contract],
  )

  // -------------------------------------------------------------------------
  // Requery invoice_no options for the resolved contract (P || S).
  // -------------------------------------------------------------------------
  const refetchInvoices = async (contractNo: string) => {
    if (!contractNo) {
      setInvoiceOptions([])
      return
    }
    try {
      const res = await fetchInvoices({ contractNo }).unwrap()
      setInvoiceOptions(res.items ?? [])
    } catch {
      setInvoiceOptions([])
    }
  }

  // -------------------------------------------------------------------------
  // Load contract dropdown lists once per open.
  // -------------------------------------------------------------------------
  useEffect(() => {
    if (!open) return
    let cancelled = false
    void (async () => {
      try {
        const [p, s] = await Promise.all([
          fetchPContracts().unwrap(),
          fetchSContracts().unwrap(),
        ])
        if (cancelled) return
        setPContractOptions(p.items ?? [])
        setSContractOptions(s.items ?? [])
      } catch {
        /* combos simply stay empty; individual value still injected */
      }
    })()
    return () => {
      cancelled = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  // -------------------------------------------------------------------------
  // Hydrate on open. Deps strictly [open, mode, cvbbId].
  // -------------------------------------------------------------------------
  useEffect(() => {
    if (!open) return
    let cancelled = false

    const init = async () => {
      setInvoiceOptions([])
      // New: empty, today's date.
      if (mode === 'new' || cvbbId == null) {
        setDraft({ ...EMPTY_DRAFT, cvbb_date: new Date().toISOString().slice(0, 10) })
        return
      }
      // Edit / Copy: load snapshot.
      setHydrating(true)
      try {
        const res = await fetchDetail(cvbbId).unwrap()
        if (cancelled) return
        const row: CvbbFormSnapshot = res.data
        const contractNo = String(row.p_contract ?? row.s_contract ?? '')
        if (mode === 'copy') {
          // btnCopy: clear cvbb_no, today's date, keep type/contract/info/note/invoice.
          setDraft({
            ...EMPTY_DRAFT,
            cvbb_date: new Date().toISOString().slice(0, 10),
            cvbb_type: String(row.cvbb_type ?? ''),
            p_contract: String(row.p_contract ?? ''),
            s_contract: String(row.s_contract ?? ''),
            invoice_no: String(row.invoice_no ?? ''),
            info_txt: String(row.info_txt ?? ''),
            note: String(row.note ?? ''),
          })
        } else {
          // Edit: full hydrate.
          setDraft({
            ...EMPTY_DRAFT,
            cvbb_id: row.cvbb_id != null ? String(row.cvbb_id) : '',
            cvbb_no: String(row.cvbb_no ?? ''),
            cvbb_date: String(row.cvbb_date ?? '').slice(0, 10),
            cvbb_type: String(row.cvbb_type ?? ''),
            p_contract: String(row.p_contract ?? ''),
            s_contract: String(row.s_contract ?? ''),
            invoice_no: String(row.invoice_no ?? ''),
            info_txt: String(row.info_txt ?? ''),
            note: String(row.note ?? ''),
          })
        }
        if (contractNo) await refetchInvoices(contractNo)
      } catch (err) {
        if (!cancelled) snack.error(getErrorMessage(err, 'Failed to load CVBB'))
      } finally {
        if (!cancelled) setHydrating(false)
      }
    }

    void init()
    return () => {
      cancelled = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, mode, cvbbId])

  // -------------------------------------------------------------------------
  // Contract picks — mutual exclusion + requery invoice_no.
  // -------------------------------------------------------------------------
  const handlePickPContract = (value: string) => {
    // clear s_contract + reset invoice selection when contract routing changes.
    setDraft((prev) => ({ ...prev, p_contract: value, s_contract: '', invoice_no: '' }))
    void refetchInvoices(value)
  }

  const handlePickSContract = (value: string) => {
    setDraft((prev) => ({ ...prev, s_contract: value, p_contract: '', invoice_no: '' }))
    void refetchInvoices(value)
  }

  const set = <K extends keyof DraftState>(key: K, value: string) =>
    setDraft((prev) => ({ ...prev, [key]: value }))

  // -------------------------------------------------------------------------
  // Save
  // -------------------------------------------------------------------------
  const savingRef = useRef(false)
  const handleSave = async () => {
    if (!canEdit || saving || hydrating || savingRef.current) return

    // Validation (verbatim English, ordered per Access strCheck).
    if (!draft.cvbb_no.trim()) {
      snack.warning('Please type CVBB No!')
      return
    }
    if (!draft.cvbb_type.trim()) {
      snack.warning('Please type CVBB Type!')
      return
    }
    if (!draft.cvbb_date) {
      snack.warning('Please choose CVBB Date!')
      return
    }
    if (draft.p_contract && draft.s_contract) {
      snack.warning('Please choose P-contract or S-contract!')
      return
    }
    if (!draft.p_contract && !draft.s_contract) {
      snack.warning('Please choose P-contract or S-contract!')
      return
    }

    savingRef.current = true
    try {
      const contractNo = (draft.p_contract || draft.s_contract || '').trim()
      const body: CvbbWrite = {
        cvbb_no: draft.cvbb_no.trim(),
        cvbb_date: draft.cvbb_date || null,
        cvbb_type: draft.cvbb_type || null,
        contract_no: contractNo || null,
        info_txt: draft.info_txt || null,
        invoice_no: draft.invoice_no || null,
        note: draft.note || null,
      }

      if (mode === 'edit' && cvbbId != null) {
        await updateCvbb({ id: cvbbId, body }).unwrap()
        snack.success('Successfully Updated')
      } else {
        await createCvbb(body).unwrap()
        snack.success('Successfully Added')
      }
      onSaved()
      onClose()
    } catch (err) {
      snack.error(getErrorMessage(err, 'Failed to save CVBB'))
    } finally {
      savingRef.current = false
    }
  }

  const handleClose = () => {
    if (saving) return
    onClose()
  }

  // -------------------------------------------------------------------------
  // Render helpers
  // -------------------------------------------------------------------------
  const titleText =
    mode === 'new'
      ? 'Inbound Logistics - CVBB - New'
      : mode === 'copy'
        ? 'Inbound Logistics - CVBB - Copy'
        : 'Inbound Logistics - CVBB - Edit'

  const canSave = canEdit && !saving && !hydrating

  /** Flat Autocomplete for a {value,label} lookup; injects the saved value. */
  const renderCombo = (params: {
    label: string
    value: string
    options: Opt[]
    onPick: (v: string) => void
    sx?: SxProps<Theme>
    disabled?: boolean
    collapseEqual?: boolean
    error?: boolean
  }) => {
    const { label, value, options, onPick, sx, disabled, collapseEqual, error } = params
    const sel = options.find((o) => o.value === value) ?? (value ? { value, label: value } : null)
    const merged = sel && !options.some((o) => o.value === sel.value) ? [sel, ...options] : options
    return (
      <Autocomplete
        size="small"
        sx={sx}
        options={merged}
        value={sel}
        disabled={disabled}
        onChange={(_e, v) => onPick(v?.value ?? '')}
        getOptionLabel={(o) => {
          if (!o) return ''
          const code = String(o.value ?? '')
          const lab = String(o.label ?? '')
          if (!lab || lab === code) return code
          if (collapseEqual && code === lab) return code
          return `${code} — ${lab}`
        }}
        isOptionEqualToValue={(a, b) => a.value === b.value}
        renderInput={(p) => <TextField {...p} label={label} error={error} />}
      />
    )
  }

  return (
    <Dialog
      open={open}
      onClose={(_e, reason) => {
        if (saving) return
        if (reason === 'backdropClick') return
        handleClose()
      }}
      maxWidth="md"
      fullWidth
      scroll="paper"
    >
      <DialogTitle sx={dialogTitleSx}>
        <Typography component="span" noWrap title={titleText} sx={dialogTitleTextSx}>
          {titleText}
        </Typography>
        <Stack direction="row" spacing={0.5} sx={dialogTitleActionsSx}>
          <Button
            type="button"
            variant="contained"
            size="small"
            color="success"
            onClick={() => void handleSave()}
            disabled={!canSave}
            sx={dialogHeaderBtnSx}
          >
            {saving ? <CircularProgress size={14} color="inherit" /> : 'Save'}
          </Button>
          <Button
            type="button"
            variant="outlined"
            size="small"
            onClick={handleClose}
            disabled={saving}
            sx={dialogHeaderBtnSx}
          >
            Cancel
          </Button>
        </Stack>
      </DialogTitle>
      <Divider />
      <DialogContent dividers sx={compactDialogContentSx}>
        {!canEdit && (
          <Alert severity="info" sx={{ mb: 1.5, py: 0.25 }}>
            Read-only — you don&apos;t have edit permission for CVBB.
          </Alert>
        )}

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))' },
            columnGap: 1.5,
            rowGap: 1.25,
            alignItems: 'start',
          }}
        >
          {/* Row 1 — cvbb_id (readonly) */}
          <TextField
            label="CVBB ID"
            size="small"
            fullWidth
            value={draft.cvbb_id}
            disabled
            sx={COL1}
          />

          {/* Row 2 — cvbb_no | cvbb_date */}
          <TextField
            label="CVBB No *"
            size="small"
            fullWidth
            value={draft.cvbb_no}
            onChange={(e) => set('cvbb_no', e.target.value)}
            disabled={!canEdit || isEdit}
            required
            error={!draft.cvbb_no}
            sx={COL1}
          />
          <Box sx={COL2}>
            <DatePicker
              label="CVBB Date *"
              format="DD-MMM-YYYY"
              disabled={!canEdit}
              value={draft.cvbb_date ? dayjs(draft.cvbb_date) : null}
              onChange={(d) => set('cvbb_date', d && d.isValid() ? d.format('YYYY-MM-DD') : '')}
              slotProps={{
                textField: {
                  size: 'small',
                  fullWidth: true,
                  error: !draft.cvbb_date,
                  InputLabelProps: { shrink: true },
                },
              }}
            />
          </Box>

          {/* Row 3 — cvbb_type (combo) | cvbb_full_no (readonly, computed) */}
          {renderCombo({
            label: 'CVBB Type *',
            value: draft.cvbb_type,
            options: typeOptions,
            onPick: (v) => set('cvbb_type', v),
            disabled: !canEdit || isEdit,
            error: !draft.cvbb_type,
            sx: COL1,
          })}
          <TextField
            label="CVBB Full No"
            size="small"
            fullWidth
            value={cvbbFullNo}
            disabled
            sx={COL2}
          />

          {/* Row 4 — p_contract | s_contract (mutual exclusion) */}
          {renderCombo({
            label: 'Contract P',
            value: draft.p_contract,
            options: pContractOptions,
            onPick: handlePickPContract,
            disabled: !canEdit,
            sx: COL1,
          })}
          {renderCombo({
            label: 'Contract S',
            value: draft.s_contract,
            options: sContractOptions,
            onPick: handlePickSContract,
            disabled: !canEdit,
            sx: COL2,
          })}

          {/* Row 5 — p_vender | s_client (readonly, resolved from selected contract) */}
          <TextField
            label="Contract P - Vendor"
            size="small"
            fullWidth
            value={pVendorLabel}
            disabled
            sx={COL1}
          />
          <TextField
            label="Contract S - Client"
            size="small"
            fullWidth
            value={sClientLabel}
            disabled
            sx={COL2}
          />

          {/* Row 6 — invoice_no (combo, depends on chosen contract) | (empty) */}
          {renderCombo({
            label: 'Invoice No',
            value: draft.invoice_no,
            options: invoiceOptions,
            onPick: (v) => set('invoice_no', v),
            disabled: !canEdit || (!draft.p_contract && !draft.s_contract),
            sx: COL1,
          })}

          {/* Row 7 — info_txt (full width) */}
          <TextField
            label="Info"
            size="small"
            fullWidth
            multiline
            minRows={2}
            value={draft.info_txt}
            onChange={(e) => set('info_txt', e.target.value)}
            disabled={!canEdit}
            sx={SPAN2}
          />

          {/* Row 8 — note (full width) */}
          <TextField
            label="Note"
            size="small"
            fullWidth
            multiline
            minRows={2}
            value={draft.note}
            onChange={(e) => set('note', e.target.value)}
            disabled={!canEdit}
            sx={SPAN2}
          />
        </Box>
      </DialogContent>
    </Dialog>
  )
}
